/**
 * AxonEye Dashboard Widgets — CCTV intelligence widgets for YesBOS dashboards.
 *
 * 4 widget types:
 * 1. LiveCameraWidget — live camera feed thumbnail grid
 * 2. EventKpiWidget — event count KPIs (intrusions, alerts, people count)
 * 3. ActivityHeatmapWidget — hourly/daily activity heatmap
 * 4. AlertDistributionWidget — alert type distribution (pie/bar)
 *
 * All widgets are self-contained composites designed to be rendered
 * by the dashboard WidgetRenderer.
 *
 * Task: J5.7 (Dashboard widgets: live camera, event KPIs, activity heatmap, alert distribution)
 */

import * as React from 'react';
import { cn } from '../../lib/utils';
import {
  Camera,
  CameraOff,
  AlertTriangle,
  Users,
  ShieldAlert,
  Clock,
  Activity,
  Eye,
  CircleDot,
  Car,
  HardHat,
  Footprints,
  RefreshCw,
  Maximize2,
  BarChart3,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';

// ─── Shared Types ────────────────────────────────────────────────

export type AlertSeverity = 'critical' | 'warning' | 'info';

export type EventType =
  | 'zone_intrusion'
  | 'loitering'
  | 'people_count'
  | 'after_hours'
  | 'vehicle_entry'
  | 'vehicle_exit'
  | 'ppe_violation'
  | 'camera_offline';

// ─── Widget 1: Live Camera ───────────────────────────────────────

export interface CameraFeed {
  id: string;
  name: string;
  location?: string;
  thumbnailUrl?: string;
  isOnline: boolean;
  lastFrame?: string;
  peopleCount?: number;
  hasAlert?: boolean;
}

export interface LiveCameraWidgetProps {
  cameras: CameraFeed[];
  maxVisible?: number;
  onCameraClick?: (cameraId: string) => void;
  onExpandAll?: () => void;
  className?: string;
}

export function LiveCameraWidget({
  cameras,
  maxVisible = 4,
  onCameraClick,
  onExpandAll,
  className,
}: LiveCameraWidgetProps) {
  const visible = cameras.slice(0, maxVisible);
  const onlineCount = cameras.filter((c) => c.isOnline).length;

  return (
    <div className={cn('rounded-lg border bg-background p-4', className)}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Camera className="h-4 w-4 text-muted-foreground" />
          Live Cameras
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {onlineCount}/{cameras.length} online
          </span>
          {onExpandAll && (
            <button
              onClick={onExpandAll}
              className="rounded p-1 hover:bg-muted transition-colors"
            >
              <Maximize2 className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {visible.map((cam) => (
          <button
            key={cam.id}
            onClick={() => onCameraClick?.(cam.id)}
            className={cn(
              'relative aspect-video rounded-md overflow-hidden border transition-colors',
              cam.isOnline ? 'hover:border-primary' : 'opacity-60',
              cam.hasAlert && 'ring-2 ring-red-500'
            )}
          >
            {/* Thumbnail / placeholder */}
            {cam.thumbnailUrl ? (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${cam.thumbnailUrl})` }}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-muted">
                {cam.isOnline ? (
                  <Camera className="h-6 w-6 text-muted-foreground" />
                ) : (
                  <CameraOff className="h-6 w-6 text-red-400" />
                )}
              </div>
            )}

            {/* Overlay */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
              <p className="text-[10px] font-medium text-white truncate">{cam.name}</p>
              {cam.location && (
                <p className="text-[9px] text-white/70 truncate">{cam.location}</p>
              )}
            </div>

            {/* Status dot */}
            <div className="absolute right-1.5 top-1.5">
              <div
                className={cn(
                  'h-2 w-2 rounded-full',
                  cam.isOnline ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                )}
              />
            </div>

            {/* People count badge */}
            {cam.peopleCount !== undefined && cam.peopleCount > 0 && (
              <div className="absolute left-1.5 top-1.5 flex items-center gap-0.5 rounded bg-black/60 px-1 py-0.5">
                <Users className="h-2.5 w-2.5 text-white" />
                <span className="text-[9px] font-medium text-white">{cam.peopleCount}</span>
              </div>
            )}

            {/* Alert indicator */}
            {cam.hasAlert && (
              <div className="absolute right-1.5 bottom-8">
                <AlertTriangle className="h-4 w-4 text-red-500 animate-pulse" />
              </div>
            )}
          </button>
        ))}
      </div>

      {cameras.length > maxVisible && (
        <button
          onClick={onExpandAll}
          className="mt-2 w-full text-center text-xs text-primary hover:underline"
        >
          View all {cameras.length} cameras
        </button>
      )}
    </div>
  );
}

// ─── Widget 2: Event KPIs ────────────────────────────────────────

export interface EventKpi {
  type: EventType;
  label: string;
  count: number;
  previousCount: number;
  severity: AlertSeverity;
}

export interface EventKpiWidgetProps {
  events: EventKpi[];
  totalEventsToday?: number;
  className?: string;
}

const EVENT_ICONS: Record<EventType, React.ElementType> = {
  zone_intrusion: ShieldAlert,
  loitering: Footprints,
  people_count: Users,
  after_hours: Clock,
  vehicle_entry: Car,
  vehicle_exit: Car,
  ppe_violation: HardHat,
  camera_offline: CameraOff,
};

const SEVERITY_COLORS: Record<AlertSeverity, string> = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

export function EventKpiWidget({
  events,
  totalEventsToday,
  className,
}: EventKpiWidgetProps) {
  return (
    <div className={cn('rounded-lg border bg-background p-4', className)}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Activity className="h-4 w-4 text-muted-foreground" />
          Event Summary
        </h3>
        {totalEventsToday !== undefined && (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            {totalEventsToday} today
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        {events.map((evt) => {
          const Icon = EVENT_ICONS[evt.type] ?? Activity;
          const diff = evt.count - evt.previousCount;
          const pctChange = evt.previousCount > 0
            ? ((diff / evt.previousCount) * 100)
            : 0;

          return (
            <div
              key={evt.type}
              className={cn('rounded-md p-2.5', SEVERITY_COLORS[evt.severity])}
            >
              <div className="flex items-center gap-1.5">
                <Icon className="h-4 w-4" />
                <span className="text-xs font-medium truncate">{evt.label}</span>
              </div>
              <div className="mt-1 flex items-end justify-between">
                <span className="text-xl font-bold">{evt.count}</span>
                {diff !== 0 && (
                  <span className="flex items-center gap-0.5 text-[10px]">
                    {diff > 0 ? (
                      <ArrowUpRight className="h-2.5 w-2.5" />
                    ) : (
                      <ArrowDownRight className="h-2.5 w-2.5" />
                    )}
                    {Math.abs(pctChange).toFixed(0)}%
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Widget 3: Activity Heatmap ──────────────────────────────────

export interface HeatmapCell {
  hour: number;
  day: number;
  value: number;
}

export interface ActivityHeatmapWidgetProps {
  data: HeatmapCell[];
  maxValue?: number;
  dayLabels?: string[];
  className?: string;
}

const DEFAULT_DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function getHeatColor(value: number, max: number): string {
  if (value === 0) return 'bg-muted';
  const intensity = value / max;
  if (intensity < 0.25) return 'bg-green-200 dark:bg-green-900/40';
  if (intensity < 0.5) return 'bg-yellow-200 dark:bg-yellow-900/40';
  if (intensity < 0.75) return 'bg-orange-300 dark:bg-orange-800/50';
  return 'bg-red-400 dark:bg-red-700/60';
}

export function ActivityHeatmapWidget({
  data,
  maxValue,
  dayLabels = DEFAULT_DAY_LABELS,
  className,
}: ActivityHeatmapWidgetProps) {
  const max = maxValue ?? Math.max(...data.map((d) => d.value), 1);

  // Build lookup
  const lookup = React.useMemo(() => {
    const map = new Map<string, number>();
    data.forEach((d) => map.set(`${d.day}-${d.hour}`, d.value));
    return map;
  }, [data]);

  return (
    <div className={cn('rounded-lg border bg-background p-4', className)}>
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
        <BarChart3 className="h-4 w-4 text-muted-foreground" />
        Activity Heatmap
      </h3>

      <div className="overflow-x-auto">
        <div className="min-w-[500px]">
          {/* Hour labels */}
          <div className="flex pl-10 mb-1">
            {HOURS.filter((h) => h % 3 === 0).map((h) => (
              <span
                key={h}
                className="text-[9px] text-muted-foreground"
                style={{ width: `${(3 / 24) * 100}%` }}
              >
                {h.toString().padStart(2, '0')}
              </span>
            ))}
          </div>

          {/* Grid */}
          {dayLabels.map((day, dayIdx) => (
            <div key={dayIdx} className="flex items-center gap-1 mb-0.5">
              <span className="w-9 text-[10px] text-muted-foreground text-right pr-1">
                {day}
              </span>
              <div className="flex flex-1 gap-px">
                {HOURS.map((hour) => {
                  const value = lookup.get(`${dayIdx}-${hour}`) ?? 0;
                  return (
                    <div
                      key={hour}
                      className={cn(
                        'flex-1 h-4 rounded-[2px] transition-colors',
                        getHeatColor(value, max)
                      )}
                      title={`${day} ${hour}:00 — ${value} events`}
                    />
                  );
                })}
              </div>
            </div>
          ))}

          {/* Legend */}
          <div className="flex items-center justify-end gap-1 mt-2">
            <span className="text-[9px] text-muted-foreground">Less</span>
            <div className="h-3 w-3 rounded-[2px] bg-muted" />
            <div className="h-3 w-3 rounded-[2px] bg-green-200 dark:bg-green-900/40" />
            <div className="h-3 w-3 rounded-[2px] bg-yellow-200 dark:bg-yellow-900/40" />
            <div className="h-3 w-3 rounded-[2px] bg-orange-300 dark:bg-orange-800/50" />
            <div className="h-3 w-3 rounded-[2px] bg-red-400 dark:bg-red-700/60" />
            <span className="text-[9px] text-muted-foreground">More</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Widget 4: Alert Distribution ────────────────────────────────

export interface AlertBucket {
  type: EventType;
  label: string;
  count: number;
  color: string;
}

export interface AlertDistributionWidgetProps {
  alerts: AlertBucket[];
  totalAlerts?: number;
  className?: string;
}

export function AlertDistributionWidget({
  alerts,
  totalAlerts,
  className,
}: AlertDistributionWidgetProps) {
  const total = (totalAlerts ?? alerts.reduce((sum, a) => sum + a.count, 0)) || 1;
  const sorted = [...alerts].sort((a, b) => b.count - a.count);

  return (
    <div className={cn('rounded-lg border bg-background p-4', className)}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          Alert Distribution
        </h3>
        <span className="text-xs text-muted-foreground">{total} total</span>
      </div>

      {/* Stacked bar */}
      <div className="h-6 flex overflow-hidden rounded-full mb-3">
        {sorted.map((a) => {
          const pct = (a.count / total) * 100;
          if (pct < 1) return null;
          return (
            <div
              key={a.type}
              className="h-full transition-all duration-500"
              style={{ width: `${pct}%`, backgroundColor: a.color }}
              title={`${a.label}: ${a.count} (${pct.toFixed(1)}%)`}
            />
          );
        })}
      </div>

      {/* Legend list */}
      <div className="space-y-1.5">
        {sorted.map((a) => {
          const pct = (a.count / total) * 100;
          const Icon = EVENT_ICONS[a.type] ?? Activity;
          return (
            <div key={a.type} className="flex items-center gap-2 text-sm">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: a.color }}
              />
              <Icon className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="flex-1 truncate">{a.label}</span>
              <span className="font-medium">{a.count}</span>
              <span className="text-xs text-muted-foreground w-10 text-right">
                {pct.toFixed(0)}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
